/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Checkbox, Form, Input as AntdInput } from 'antd';

import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

import { destinationDisplayNames, DestinationType } from './constants';
import { CreateAlertWizardFooter } from './CreateAlertWizardFooter';

const { TextArea } = AntdInput;

type SetupConnectionStepProps = {
  destinationType: DestinationType;
  name: string;
  description: string;
  webhookUrl: string;
  token: string;
  disableTls: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onWebhookUrlChange: (value: string) => void;
  onTokenChange: (value: string) => void;
  onDisableTlsChange: (value: boolean) => void;
  onNext: () => void;
  onCancel: () => void;
};

export function SetupConnectionStep({
  destinationType,
  name,
  description,
  webhookUrl,
  token,
  disableTls,
  onNameChange,
  onDescriptionChange,
  onWebhookUrlChange,
  onTokenChange,
  onDisableTlsChange,
  onNext,
  onCancel,
}: SetupConnectionStepProps) {
  const { t } = useMimicTranslation('notificationManagement');
  const { t: tGlobal } = useMimicTranslation('global');
  const displayName = destinationDisplayNames[destinationType];
  const needsToken = destinationType === 'exabeam' || destinationType === 'splunk';

  // All fields are required in both create and edit mode
  const isValid = name.trim().length > 0 && webhookUrl.trim().length > 0 && (!needsToken || token.trim().length > 0);

  return (
    <>
      <div className="create-alert-wizard-content">
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 0' }}>
          <Title level={3}>{t('setupConnection.title', { destination: displayName })}</Title>
          <Text type="subtle" size="md">
            {t('setupConnection.description', { destination: displayName })}
          </Text>

          <Form layout="vertical" style={{ marginTop: '32px' }}>
            <Form.Item label={t('setupConnection.connectionName')} required>
              <AntdInput
                placeholder={tGlobal('forms.required')}
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                data-testid="alert-connection-name"
                style={{ backgroundColor: '#ffffff' }}
              />
            </Form.Item>

            <Form.Item label={t('setupConnection.descriptionLabel')}>
              <TextArea
                placeholder={t('setupConnection.descriptionPlaceholder')}
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                rows={5}
                data-testid="alert-connection-description"
                style={{ backgroundColor: '#ffffff' }}
              />
            </Form.Item>

            <Form.Item label={t('setupConnection.webhookUrl')} required>
              <AntdInput
                placeholder={tGlobal('forms.required')}
                value={webhookUrl}
                onChange={(e) => onWebhookUrlChange(e.target.value)}
                data-testid="alert-webhook-url"
                style={{ backgroundColor: '#ffffff' }}
              />
            </Form.Item>

            {needsToken && (
              <Form.Item label={t('setupConnection.token')} required>
                <AntdInput.Password
                  placeholder={tGlobal('forms.required')}
                  value={token}
                  onChange={(e) => onTokenChange(e.target.value)}
                  data-testid="alert-token"
                  style={{ backgroundColor: '#ffffff' }}
                />
              </Form.Item>
            )}

            {destinationType === 'splunk' && (
              <Form.Item>
                <Checkbox
                  checked={disableTls}
                  onChange={(e) => onDisableTlsChange(e.target.checked)}
                  data-testid="alert-disable-tls"
                >
                  <Text size="md">{t('setupConnection.disableTls')}</Text>
                </Checkbox>
              </Form.Item>
            )}
          </Form>
        </div>
      </div>
      <CreateAlertWizardFooter
        onNext={onNext}
        onPrevious={onCancel}
        nextLabel={t('setupConnection.nextLabel')}
        previousLabel={t('cancel')}
        nextDisabled={!isValid}
      />
    </>
  );
}
